<script>
    import { preventDefault } from 'svelte/legacy';

import {
    modalState, hideModal
} from '@app/store/modalStore.svelte.js';
import {
    eventGroupState
} from '@app/store/eventGroupStore.svelte.js';
import {
    markerState
} from '@app/store/markerStore.svelte.js';
import {
    insertHistoryAction
} from '@app/store/historyStore.svelte.js';
import {
    projectState
} from '@app/store/projectStore.svelte.js';
import { toast } from '@zerodevx/svelte-toast';
import {
    fade
} from 'svelte/transition';
import orderByTime from "@cclib/functions/eventGroups/orderByTime.js";
import tcRound from "@cclib/functions/eventGroups/tcRound.js";
import * as m from '@app/paraglide/messages.js';

let frames = $state(2), minFrameGap = $state(0), maxFrameGap = $state(6), shotChangeAware = $state(true), processing = $state(false);

function insertBlankFrames() {
    try {
        processing = true;

        toast.push(m.inserting_blank_frames(), {
            classes: ["toast-info"]
        });

        let frameGapSec = (frames/projectState.frameRate);
        let minFrameGapSec = minFrameGap/projectState.frameRate;
        let maxFrameGapSec = maxFrameGap/projectState.frameRate;

        eventGroupState[projectState.selected] = orderByTime(eventGroupState[projectState.selected]);
        eventGroupState[projectState.selected] = tcRound(eventGroupState[projectState.selected], projectState.frameRate, projectState.dropFrame);

        eventGroupState[projectState.selected].events.forEach((event, index, events) => {
            if (index === events.length-1){
                return;
            }

            if (!event.end || event.end < 0){
                return;
            }

            let eventFrameGap = events[index+1].start - event.end;
            if (eventFrameGap <= maxFrameGapSec && eventFrameGap >= minFrameGapSec){
                //console.log("Event Frame Gap: ",eventFrameGap);
                if (shotChangeAware){
                    //console.log("Shot Change Aware is true");
                    let marker = markerState.lists[0].markers.find(mark =>{
                        return parseFloat(mark.time).toFixed(5) === parseFloat(events[index].end).toFixed(5) || parseFloat(mark.time).toFixed(5) === parseFloat(events[index+1].start).toFixed(5);
                    });

                    //console.log("Marker Found:", marker);

                    if (marker){
                        if (parseFloat(marker.time).toFixed(5) === parseFloat(events[index+1].start).toFixed(5)){
                            if (events[index+1].start-frameGapSec <= event.start){
                                events[index+1].start = event.end + frameGapSec;
                            } else {
                                events[index].end = events[index+1].start-frameGapSec;
                            }
                        } else {
                            if (events[index].end+frameGapSec >= events[index+1].end){
                                events[index].end = events[index+1].start-frameGapSec;
                            } else {
                                events[index+1].start = event.end + frameGapSec;
                            }
                        }
                    } else {
                        //console.log("No marker found:");
                        if (events[index+1].start-frameGapSec <= event.start){
                            events[index+1].start = event.end + frameGapSec;
                        } else {
                            events[index].end = events[index+1].start-frameGapSec;
                        }
                    }
                } else {
                    if (events[index+1].start-frameGapSec <= event.start){
                        events[index+1].start = event.end + frameGapSec;
                    } else {
                        events[index].end = events[index+1].start-frameGapSec;
                    }
                }
            }
        });

        eventGroupState[projectState.selected] = eventGroupState[projectState.selected];

        insertHistoryAction({
            name: "insert frame gap", //action name
            eventGroup: projectState.selected,
            snapshots: [{
                store: "eventGroupState",
                value: JSON.stringify(eventGroupState)
            }]
        });

        toast.push(m.inserting_blank_frames_success(), {
            classes: ["toast-success"]
        });
    } catch (err) {
        console.error(err, err.message);
        toast.push(m.inserting_blank_frames_failed(), {
            classes: ["toast-danger"]
        });
    } finally {
        hideModal();
    }
}
</script>

<div transition:fade={{duration: 100}} 
    class="modal {modalState.selected === 'blankFrames' ? 'show d-block' : ''}" 
    role="dialog" 
    aria-labelledby="blankFramesModalTitle"
    aria-describedby="blankFramesModalDesc"
    tabindex="-1" 
    id="blankFramesModal">
    <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="blankFramesModalTitle">{m.insert_blank_frames_title()}</h4>
                <button type="button" class="btn-close" id="closeBlankFramesModal" aria-label={m.close_dialog()} onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <p id="blankFramesModalDesc" class="visually-hidden">{m.blank_frames_description()}</p>
                <form onsubmit={preventDefault(insertBlankFrames)}>
                    <div class="row">
                        <div class="col-12">
                            <label class="form-label" for="framesInput">{m.frames_to_insert()}</label>
                            <input class="form-control" id="framesInput" type="number" min="0" step="1" 
                                aria-describedby="framesHelp" bind:value={frames}>
                            <div id="framesHelp" class="form-text">{m.frames_to_insert_help()}</div>
                        </div>
                        <div class="col my-3">
                            <label class="form-label" for="minFrameGap">{m.minimum_frame_gap()}</label>
                            <input class="form-control" id="minFrameGap" type="number" min="0" step="1" 
                                aria-describedby="minGapHelp" bind:value={minFrameGap}>
                            <div id="minGapHelp" class="form-text">{m.minimum_gap_allowed()}</div>
                        </div>
                        <div class="col my-3">
                            <label class="form-label" for="maxFrameGap">{m.maximum_frame_gap()}</label>
                            <input class="form-control" id="maxFrameGap" type="number" min="0" step="1" 
                                aria-describedby="maxGapHelp" bind:value={maxFrameGap}>
                            <div id="maxGapHelp" class="form-text">{m.maximum_gap_allowed()}</div>
                        </div>
                        <div class="mb-3 col-12">
                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" id="shotChangeAware" 
                                    aria-describedby="shotChangeHelp" bind:checked={shotChangeAware}>
                                <label class="form-check-label" for="shotChangeAware">{m.shot_change_aware()}</label>
                                <div id="shotChangeHelp" class="form-text">{m.shot_change_aware_help()}</div>
                            </div>
                        </div>
                    </div>
                    <button class="btn btn-primary float-end" id="applyBlankFrames" type="button" 
                        onclick={insertBlankFrames} 
                        disabled={processing}
                        aria-busy={processing}>
                        {processing ? m.processing() : m.apply()}
                    </button>
                </form>
            </div>
        </div>
    </div>
</div>
